import React, {type ComponentType, useState} from 'react';

import {FullHTMLLink} from 'app/components/general/FullHTMLLink';
import {AttachTooltip} from 'app/general/overlays';
import {isMemberTeam} from 'app/teams/selectors';
import {gt} from 'app/utils/i18n';

import type {
  ContentType,
  TeamMembershipSerializedType,
  TeamSerializedType,
} from 'sweet-types';

type BaseObject = {
  team_memberships: Array<TeamMembershipSerializedType>;
  html_url: string;
};
export type ListItemProps<T> = {
  object: T;
  team: TeamSerializedType | null;
  remove: (team: TeamSerializedType, item: T) => Promise<any>;
  refetchList: () => Promise<any>;
};
export type Props<T> = ListItemProps<T> & {
  contentType: 'procedure' | 'process' | 'policy' | 'quiz';
  ListItem: ComponentType<{
    object: T;
    refreshData: () => Promise<unknown>;
    toggleRelation: () => void;
    activeRelations: Record<string, boolean> | Record<ContentType, boolean>;
  }>;
};

export const TeamListItem = <T extends BaseObject>({
  team,
  contentType,
  object,
  ListItem,
  refetchList,
  remove,
}: Props<T>) => {
  const [isRemoving, setIsRemoving] = useState(false);

  const removeDocument = () => {
    setIsRemoving(true);

    if (team != null) {
      remove(team, object).then(() => setIsRemoving(false));
    } else {
      setIsRemoving(false);
    }
  };

  const renderRemoveButton = (object: T) => {
    if (team == null) {
      return null;
    }

    const membership = isMemberTeam(object.team_memberships, team);

    if (membership == null) {
      // not a member of the team.
      return null;
    }

    return (
      <AttachTooltip
        tooltipContent={
          membership.direct ? (
            <div>
              <div className="mb1">{gt('Remove from team')}</div>
              <div className="small">
                {gt(
                  'This will remove the team from all of the tasks related to this %(contentType)s',
                  {
                    contentType,
                  }
                )}
              </div>
            </div>
          ) : (
            <div>
              <div className="mb1">
                {membership.via_process ? (
                  <i className="far fa-copy mr-1" />
                ) : (
                  <i className="far fa-folder mr-1" />
                )}
                {membership.via_name}
              </div>
              <div className="small">
                {membership.via_process
                  ? gt(
                      'This %(contentType)s was added to the team via a process',
                      {
                        contentType,
                      }
                    )
                  : gt(
                      'This %(contentType)s was added to the team via a folder',
                      {
                        contentType,
                      }
                    )}
              </div>
            </div>
          )
        }
      >
        {(handlers) => (
          <span {...handlers}>
            {membership.direct ? (
              <button
                disabled={isRemoving}
                onClick={removeDocument}
                className="btn btn-link py-0 px-1 text-dark"
              >
                {isRemoving ? (
                  <i className="fa fa-fw fa-circle-notch fa-spin" />
                ) : (
                  <i className="fa fa-fw fa-times" />
                )}
              </button>
            ) : (
              <FullHTMLLink
                html_url={membership.html_url}
                className="btn btn-link py-0 px-1 text-dark"
              >
                {membership.via_process ? (
                  <i className="far fa-fw fa-copy" />
                ) : (
                  <i className="far fa-fw fa-folder" />
                )}
              </FullHTMLLink>
            )}
          </span>
        )}
      </AttachTooltip>
    );
  };
  return (
    <div className="row" key={object.html_url}>
      <div className="col">
        <ListItem
          object={object}
          refreshData={refetchList}
          toggleRelation={() => null}
          activeRelations={{}}
        />
      </div>
      <div className="col-auto border-bottom d-flex align-items-center">
        <div className="py-1">
          {team != null && team.can_edit ? renderRemoveButton(object) : null}
        </div>
      </div>
    </div>
  );
};
